import { IconButton, Tag, Tooltip } from '@ads/components-react';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import type { IndividualPaymentPaymentSerialViewModel } from '@/shared/api/generated';
import { Skeleton } from '@/shared/ui/atoms';
import { Drawer } from '@/shared/ui/organisms/Drawer';

import { useParcelSerials } from '../../../hooks/useParcelSerials';
import { formatContractDate } from '../../../utils/formatContractDate';
import { EMPTY_VALUE } from '../../../utils/formatCurrency';
import { formatDateTime } from '../../../utils/formatDateTime';
import { CopyableField } from '../../molecules/CopyableField';

/** Props de `ParcelBilletHistoryDrawer`. */
export interface ParcelBilletHistoryDrawerProps {
  /** `null` mantém o painel fechado e a query desabilitada. */
  readonly parcelId: number | null;
  /** Contexto do escopo, exibido acima do título. */
  readonly subtitle?: string | undefined;
  readonly onClose: () => void;
  /**
   * Reintegra a parcela — ação por boleto, delegada à página.
   *
   * Recebe o `serial` do boleto do cartão, que a request aceita para escolher o
   * alvo do reenvio.
   */
  readonly onReintegrate: (serial?: number) => void;
}

/** Par rótulo-valor da grade de metadados do boleto. */
function Field({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="flex min-w-[8rem] flex-1 flex-col gap-quark">
      <span className="ts-caption-xxs text-ctx-content-placeholder">{label}</span>
      <span className="break-words ts-paragraph-xxs text-ctx-content-base">{value}</span>
    </div>
  );
}

function BilletCard({
  serial,
  onReintegrate,
}: {
  readonly serial: IndividualPaymentPaymentSerialViewModel;
  readonly onReintegrate: (serial?: number) => void;
}) {
  const { t } = useTranslation('individualPayment');

  return (
    <li className="flex flex-col gap-micro rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-mini">
      <div className="flex flex-wrap items-center gap-nano">
        <span className="ts-subtitle-xxs tabular-nums text-ctx-content-title">
          {serial.serial ?? EMPTY_VALUE}
        </span>
        <Tag
          label={serial.active ? t('parcels.serials.active') : t('parcels.serials.cancelled')}
          size="sm"
          status={serial.active ? 'positive' : 'negative'}
          custom={{ text: 'text-nowrap' }}
        />
        {serial.paid ? (
          <Tag
            label={t('parcelEdit.billets.paid')}
            size="sm"
            status="highlight"
            custom={{ text: 'text-nowrap' }}
          />
        ) : null}
        <span className="grow" />
        <IconButton
          icon={ArrowPathIcon}
          hierarchy="tertiary"
          size="sm"
          ariaLabel={t('parcels.operations.reintegrate')}
          onClick={() => onReintegrate(serial.serial)}
        />
        {/* O PDF já vem como URL na própria resposta de `serials`, então o
            download não passa por endpoint nenhum — é só abrir. `noopener`
            porque a URL é externa (provedor de pagamento). */}
        <Tooltip
          title={
            serial.pdfFileUrl
              ? t('parcels.operations.downloadPdf')
              : t('parcels.serials.pdfUnavailable')
          }
        >
          <IconButton
            icon={ArrowDownTrayIcon}
            hierarchy="tertiary"
            size="sm"
            ariaLabel={t('parcels.operations.downloadPdf')}
            disabled={!serial.pdfFileUrl}
            onClick={() => {
              if (serial.pdfFileUrl) window.open(serial.pdfFileUrl, '_blank', 'noopener');
            }}
          />
        </Tooltip>
      </div>

      <div className="flex flex-wrap gap-xxxs">
        <Field label={t('parcelEdit.billets.issuedAt')} value={formatDateTime(serial.nsuDate)} />
        <Field
          label={t('parcelEdit.billets.station')}
          value={[serial.station, serial.typeAmbient].filter(Boolean).join('  ·  ') || EMPTY_VALUE}
        />
        <Field label={t('parcelEdit.billets.situation')} value={serial.situation ?? EMPTY_VALUE} />
        {/* A quarta célula troca de assunto conforme o boleto: num boleto vivo o
            que importa é o pagamento; num cancelado, quando o cancelamento foi. */}
        <Field
          label={
            serial.active ? t('parcelEdit.billets.payment') : t('parcelEdit.billets.cancelledAt')
          }
          value={
            serial.active
              ? serial.paidDate
                ? formatContractDate(serial.paidDate)
                : t('parcelEdit.billets.unpaid')
              : formatContractDate(serial.cancellationDate)
          }
        />
      </div>

      {(serial.digitableLine || serial.ticket) && (
        <div className="flex flex-col gap-nano rounded-sm bg-ctx-layout-body p-micro">
          {serial.digitableLine && (
            <CopyableField
              label={t('parcels.serials.digitableLine')}
              value={serial.digitableLine}
            />
          )}
          {serial.ticket && (
            <CopyableField label={t('parcels.serials.ticket')} value={serial.ticket} />
          )}
        </div>
      )}
    </li>
  );
}

/**
 * Painel lateral com os boletos emitidos de uma parcela.
 *
 * É a ação "Histórico de boletos" da régua da tela de edição, e a fonte é
 * `GET /individualpayment/parcels/{parcelId}/serials` — o **mesmo** endpoint da
 * sub-tabela da listagem de parcelas, não as rotas de boleto do PecegePay: estas
 * são indexadas por `courseFinPersonPaymentId`, que a tela não conhece.
 *
 * O layout difere do `ParcelSerialsPanel` de propósito. Lá o painel é recuado
 * dentro de uma linha larga e cabe uma sub-tabela de seis colunas; num drawer a
 * largura é fixa e estreita, então cada boleto vira um cartão com os metadados
 * em grade fluida. Os dados e o hook são os mesmos.
 *
 * Segue o frame `L0ez9` de `design/payment.pen`.
 */
export function ParcelBilletHistoryDrawer({
  parcelId,
  subtitle,
  onClose,
  onReintegrate,
}: ParcelBilletHistoryDrawerProps) {
  const { t } = useTranslation('individualPayment');
  const isOpen = parcelId !== null;

  // O hook só dispara com o painel aberto, e a `queryKey` é a mesma da
  // sub-tabela da listagem: reabrir dentro da janela de frescor não gera tráfego.
  const { serials, isLoading, isError } = useParcelSerials(parcelId ?? 0, isOpen);

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={t('parcelEdit.billets.title')}
      {...(subtitle === undefined ? {} : { subtitle })}
    >
      {isLoading ? (
        <ul className="flex flex-col gap-micro">
          {Array.from({ length: 2 }, (_, index) => `billet-skeleton-${index}`).map(key => (
            <li
              key={key}
              className="flex flex-col gap-micro rounded-sm border-hairline border-ctx-layout-border p-mini"
            >
              <div className="flex items-center gap-nano">
                <Skeleton className="h-xs w-[8.5rem] rounded-sm" />
                <Skeleton className="h-xs w-[4rem] rounded-pill" />
              </div>
              <div className="flex flex-wrap gap-xxxs">
                <Skeleton className="h-xs w-[7.5rem] rounded-sm" />
                <Skeleton className="h-xs w-[7.5rem] rounded-sm" />
                <Skeleton className="h-xs w-[7.5rem] rounded-sm" />
              </div>
            </li>
          ))}
        </ul>
      ) : isError ? (
        <div className="flex flex-col items-center gap-micro py-xs text-center">
          <span className="flex items-center justify-center rounded-pill bg-base-danger-100 p-micro">
            <ExclamationTriangleIcon className="h-xxs w-xxs text-base-danger-700" />
          </span>
          <p className="ts-paragraph-xs text-ctx-content-title">{t('parcelEdit.billets.error')}</p>
          <p className="ts-paragraph-xxs text-ctx-content-placeholder">
            {t('parcelEdit.billets.errorHint')}
          </p>
        </div>
      ) : serials.length === 0 ? (
        <div className="flex flex-col items-center gap-micro py-xs text-center">
          <span className="flex items-center justify-center rounded-pill bg-base-gray-100 p-micro">
            <TicketIcon className="h-xxs w-xxs text-ctx-content-placeholder" />
          </span>
          <p className="ts-paragraph-xs text-ctx-content-title">{t('parcels.serials.empty')}</p>
          <p className="ts-paragraph-xxs text-ctx-content-placeholder">
            {t('parcels.serials.emptyHint')}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-micro">
          <Tag
            label={t('parcelEdit.billets.count', { count: serials.length })}
            size="sm"
            status="default"
            custom={{ text: 'text-nowrap' }}
          />
          <ul className="flex flex-col gap-micro">
            {serials.map((serial, index) => (
              <BilletCard
                key={serial.id ?? `${serial.serial}-${index}`}
                serial={serial}
                onReintegrate={onReintegrate}
              />
            ))}
          </ul>
        </div>
      )}
    </Drawer>
  );
}
